import { useId } from "react";

import { BRAND_NAME } from "@/lib/brand";
import { cn } from "@/lib/cn";

type LogoProps = {
  className?: string;
  size?: number;
  /**
   * Render the primary brand tile (ink mark on a lime squircle) instead of
   * the bare mark. The tile is square; `size` is its width & height.
   */
  tile?: boolean;
};

// Mavori mark: the cursive "m" with a trading-swoosh baseline, drawn as smooth
// Béziers. Ink on the lime tile, or an opaque lime gradient for the bare
// mark. Inline SVG keeps it crisp while raster icons derive from the same shape.
// The mark is a monoline: one centreline, sheared 12° forward, stroked at a
// single width.
const LOGO_VIEWBOX_WIDTH = 978;
const LOGO_VIEWBOX_HEIGHT = 536;
const LOGO_ASPECT = LOGO_VIEWBOX_WIDTH / LOGO_VIEWBOX_HEIGHT;
const LOGO_MARK_STROKE_WIDTH = 76;
// Primary tile geometry — iOS-style squircle + mark placement, mirroring the
// 48px favicon in `apps/server/src/platform/http/favicon.ts`. Keep in sync if the
// mark or tile changes.
const TILE_VIEWBOX = 48;
const TILE_SQUIRCLE_PATH =
  "M48 24 C48 27.16 48.01 31.2 47.95 33.48 C47.92 34.76 47.89 35.57 47.81 36.48 C47.75 37.26 47.68 37.96 47.58 38.63 C47.49 39.24 47.38 39.8 47.25 40.34 C47.13 40.84 46.99 41.32 46.82 41.77 C46.67 42.19 46.49 42.59 46.29 42.97 C46.1 43.34 45.89 43.68 45.65 44.01 C45.42 44.32 45.17 44.62 44.89 44.89 C44.62 45.17 44.32 45.42 44.01 45.65 C43.68 45.89 43.34 46.1 42.97 46.29 C42.59 46.49 42.19 46.67 41.77 46.82 C41.32 46.99 40.84 47.13 40.34 47.25 C39.8 47.38 39.24 47.49 38.63 47.58 C37.96 47.68 37.26 47.75 36.48 47.81 C35.57 47.89 34.76 47.92 33.48 47.95 C31.2 48.01 27.16 48 24 48 C20.84 48 16.8 48.01 14.52 47.95 C13.24 47.92 12.43 47.89 11.52 47.81 C10.74 47.75 10.04 47.68 9.37 47.58 C8.76 47.49 8.2 47.38 7.66 47.25 C7.16 47.13 6.68 46.99 6.23 46.82 C5.81 46.67 5.41 46.49 5.03 46.29 C4.66 46.1 4.32 45.89 3.99 45.65 C3.68 45.42 3.38 45.17 3.11 44.89 C2.83 44.62 2.58 44.32 2.35 44.01 C2.11 43.68 1.9 43.34 1.71 42.97 C1.51 42.59 1.33 42.19 1.18 41.77 C1.01 41.32 0.87 40.84 0.75 40.34 C0.62 39.8 0.51 39.24 0.42 38.63 C0.32 37.96 0.25 37.26 0.19 36.48 C0.11 35.57 0.08 34.76 0.05 33.48 C-0.01 31.2 0 27.16 0 24 C0 20.84 -0.01 16.8 0.05 14.52 C0.08 13.24 0.11 12.43 0.19 11.52 C0.25 10.74 0.32 10.04 0.42 9.37 C0.51 8.76 0.62 8.2 0.75 7.66 C0.87 7.16 1.01 6.68 1.18 6.23 C1.33 5.81 1.51 5.41 1.71 5.03 C1.9 4.66 2.11 4.32 2.35 3.99 C2.58 3.68 2.83 3.38 3.11 3.11 C3.38 2.83 3.68 2.58 3.99 2.35 C4.32 2.11 4.66 1.9 5.03 1.71 C5.41 1.51 5.81 1.33 6.23 1.18 C6.68 1.01 7.16 0.87 7.66 0.75 C8.2 0.62 8.76 0.51 9.37 0.42 C10.04 0.32 10.74 0.25 11.52 0.19 C12.43 0.11 13.24 0.08 14.52 0.05 C16.8 -0.01 20.84 0 24 0 C27.16 0 31.2 -0.01 33.48 0.05 C34.76 0.08 35.57 0.11 36.48 0.19 C37.26 0.25 37.96 0.32 38.63 0.42 C39.24 0.51 39.8 0.62 40.34 0.75 C40.84 0.87 41.32 1.01 41.77 1.18 C42.19 1.33 42.59 1.51 42.97 1.71 C43.34 1.9 43.68 2.11 44.01 2.35 C44.32 2.58 44.62 2.83 44.89 3.11 C45.17 3.38 45.42 3.68 45.65 3.99 C45.89 4.32 46.1 4.66 46.29 5.03 C46.49 5.41 46.67 5.81 46.82 6.23 C46.99 6.68 47.13 7.16 47.25 7.66 C47.38 8.2 47.49 8.76 47.58 9.37 C47.68 10.04 47.75 10.74 47.81 11.52 C47.89 12.43 47.92 13.24 47.95 14.52 C48.01 16.8 48 20.84 48 24Z";
const TILE_MARK_TRANSFORM = "translate(2.88 12.425) scale(0.04319)";
// Kept byte-identical to `apps/client/src/shared/ui/icons/Logo.tsx`, which
// owns the geometry and the spacing rationale behind it.
const LOGO_MARK_PATH =
  "M682.7 249C691.2 209 696.7 182.9 705.2 142.9C717.4 85.5 683.4 39 629.3 39C575.2 39 521.4 85.5 509.2 142.9C500.7 182.9 497.3 199 488.8 239C497.3 199 500.7 182.9 509.2 142.9C521.4 85.5 487.4 39 433.3 39C379.2 39 325.4 85.5 313.2 142.9C286 242.9 263.7 405.6 186.6 469.3C146.4 502.3 94.5 506 63 478.1C31.4 450.2 30.9 400.2 61.9 359C92.7 317.9 144.5 299.6 185.1 315.7C248 340.5 293.3 366.5 356.5 391C412.3 412.6 471.5 430.4 533.5 438.4C596.4 446.6 662.9 439.6 727.3 407.5C790.6 376 845.1 325.7 891.1 271.7";

export function Logo({ className, size = 28, tile = false }: LogoProps) {
  // SVG gradient ids are document-global — derive a unique one per instance.
  const gradId = `mavoriLogo-${useId().replace(/:/g, "")}`;
  if (tile) {
    // Primary treatment: lime-gradient squircle with the mark in deep green —
    // mirrors favicon.svg so the in-page mark and the tab icon read as one.
    return (
      <svg
        width={size}
        height={size}
        viewBox={`0 0 ${TILE_VIEWBOX} ${TILE_VIEWBOX}`}
        fill="none"
        role="img"
        aria-label={`${BRAND_NAME} logo`}
        className={cn("shrink-0", className)}
      >
        <defs>
          <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stopColor="#E3FC80" />
            <stop offset="1" stopColor="#C3F53A" />
          </linearGradient>
        </defs>
        <path d={TILE_SQUIRCLE_PATH} fill={`url(#${gradId})`} />
        <g transform={TILE_MARK_TRANSFORM}>
          <path
            d={LOGO_MARK_PATH}
            fill="none"
            stroke="#0A1402"
            strokeWidth={LOGO_MARK_STROKE_WIDTH}
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </g>
      </svg>
    );
  }
  // Bare mark: the canonical lime gradient on the dark canvas.
  return (
    <svg
      width={Math.round(size * LOGO_ASPECT)}
      height={size}
      viewBox={`0 0 ${LOGO_VIEWBOX_WIDTH} ${LOGO_VIEWBOX_HEIGHT}`}
      fill="none"
      role="img"
      aria-label={`${BRAND_NAME} logo`}
      className={cn("shrink-0", className)}
    >
      <defs>
        <linearGradient
          id={gradId}
          gradientUnits="userSpaceOnUse"
          x1="26"
          y1="34"
          x2="952"
          y2="502"
        >
          <stop offset="0" stopColor="#EDFE9E" />
          <stop offset="0.52" stopColor="#CFF63C" />
          <stop offset="0.74" stopColor="#BEF236" />
          <stop offset="0.87" stopColor="#A9EC2F" />
        </linearGradient>
      </defs>
      <path
        d={LOGO_MARK_PATH}
        fill="none"
        stroke={`url(#${gradId})`}
        strokeWidth={LOGO_MARK_STROKE_WIDTH}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
